import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { Header } from '../Header';
import Sidebar from '../Sidebar';
import { ErrorBoundary } from '../ErrorBoundary';
import { MobileNav } from './MobileNav';
import AIAssistant from '../AIAssistant';
import { menuItems } from '../../services/navigation';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useCapacitorViewport } from '../../hooks/useCapacitorViewport';
import { useTheme } from '../../theme/useTheme';

interface AppShellProps {
  children: ReactNode;
}

const SIDEBAR_STORAGE_KEY = 'sidebar-open';

export function AppShell({ children }: AppShellProps) {
  const location = useLocation();
  const isLargeScreen = useMediaQuery('(min-width: 1024px)');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { resolvedTheme } = useTheme();

  useCapacitorViewport();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const stored = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (stored !== null) {
      setSidebarOpen(stored === 'true');
    } else {
      setSidebarOpen(window.innerWidth >= 1024);
    }
  }, []);

  useEffect(() => {
    if (!isLargeScreen) {
      setSidebarOpen(false);
      return;
    }

    setSidebarOpen((previous) => {
      if (previous) {
        return previous;
      }

      if (typeof window === 'undefined') {
        return true;
      }

      const stored = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
      if (stored !== null) {
        return stored === 'true';
      }

      return true;
    });
  }, [isLargeScreen]);

  useEffect(() => {
    if (typeof window === 'undefined' || !isLargeScreen) {
      return;
    }

    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarOpen));
  }, [sidebarOpen, isLargeScreen]);

  return (
    <div className="safe-area-bg flex min-h-dvh flex-col bg-app text-body">
      <Header onMenuClick={() => setSidebarOpen((prev) => !prev)} menuOpen={sidebarOpen} />
      <div className="relative flex flex-1 flex-col lg:flex-row">
        <ErrorBoundary level="section">
          <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        </ErrorBoundary>
        <div className="flex flex-1 flex-col lg:pl-0">
          <main
            className="safe-area-pb relative mx-auto flex w-full max-w-7xl flex-1 flex-col px-4 pb-12 pt-4 text-body sm:px-6 lg:px-10"
            data-testid="app-main"
          >
            <ErrorBoundary level="page">
              {children}
            </ErrorBoundary>
          </main>
        </div>
      </div>
      {!location.pathname.startsWith('/pos') && (
        <MobileNav
          items={menuItems}
          onNavigate={() => setSidebarOpen(false)}
          onMenuToggle={() => setSidebarOpen(true)}
        />
      )}
      <ErrorBoundary level="section">
        <AIAssistant />
      </ErrorBoundary>
      <ToastContainer
        position="bottom-center"
        newestOnTop
        closeOnClick
        draggable={false}
        theme={resolvedTheme}
        style={{
          marginBottom: 'calc(var(--safe-area-bottom, 0px) + var(--keyboard-height, 0px) + 1.5rem)',
        }}
      />
    </div>
  );
}
